import {useCallback, useRef} from "react";

import {isFetchBaseQueryError} from "@pg-mono/react-hook-forms-utils";

import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {getPhoneDataFromString} from "../../../form/utils/get_phone_data_from_string";
import {addFailureNotification} from "../../../notifications/utils/add_failure_notification";
import {addAppliedOffers} from "../../../offer/map/actions/add_applied_offers";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {isPropertyDetail} from "../../../property/utils/is_property_detail";
import {Country} from "../../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {hitApplicationRecommendedPropertiesSuccess} from "../../../tracking/algolytics_hits/application_hit";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {applicationLeadDataApi} from "../../api/application_lead_data_api";
import {useCreateApplicationMutation} from "../../api/create_application";
import {appendSentTinderMultiLeadApplication, setTinderMultiLeadError, setTinderMultiLeadSubmittingProperty} from "../../state/tinder_multi_lead_slice";
import {hitPropertyMultileadSent} from "../../tracking/lead/hit_property_multilead_sent";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IApplicationData} from "../../types/IApplicationData";
import {IPostApplicationPayload} from "../../types/lead/IPostApplicationPayload";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {addPropertiesToStorageSentApplication} from "../../utils/application_property_storage";
import {ApplicationSource, ApplicationSourceSection} from "../../utils/form/application_form_source";
import {getGeneratedMessage} from "../../utils/form/get_generated_message";
import {getAppErrorFromRtkError} from "../../utils/get_app_error_from_rtk_error";
import {getParsedLeadFormValues} from "../../utils/lead/get_parsed_lead_form_values";
import {addOffersToStorageSentApplication} from "../../utils/multi_lead/get_storage_sent_applications_id_list_by";
import {hitTinderMultiLeadGtmPurchase} from "./tracking/hit_tinder_multi_lead_gtm_purchase";
import {trackTinderMultiLeadSurveyError, trackTinderMultiLeadSurveyGenerate} from "./tracking/track_tinder_multi_lead_events";
import {getTinderMultiLeadContactValues, getTinderMultiLeadSentItem} from "./tinder_multi_lead_utils";

interface ISendParams {
    additionalQuestions: {value: string; label: string}[];
    property: IPropertyDetail | IRecommendedProperty;
    refererApplicationUuid: string | null;
    sourceSection: ApplicationSourceSection;
}

function getOfferRegionId(property: IPropertyDetail | IRecommendedProperty) {
    return isPropertyDetail(property) ? property.offer.region.id : property.region.id;
}

export function useTinderMultiLeadSend() {
    const dispatch = useAppDispatch();
    const isAuthenticated = useIsAuthenticated();
    const viewType = useAppSelector((store) => store.viewType.current);
    const {data: userProfile, isFetching: isUserProfileFetching, isLoading: isUserProfileLoading} = useGetUserProfileQuery();
    const [createApplication] = useCreateApplicationMutation();
    const isInFlightRef = useRef(false);

    const sendTinderMultiLeadApplication = useCallback(
        async ({additionalQuestions, property, refererApplicationUuid, sourceSection}: ISendParams): Promise<IApplicationData | null> => {
            if (isInFlightRef.current) {
                return null;
            }

            const contactValues = getTinderMultiLeadContactValues({userProfile});

            if (!contactValues) {
                dispatch(addFailureNotification("Nie znaleziono danych kontaktowych do wysłania zapytania."));
                return null;
            }

            isInFlightRef.current = true;
            dispatch(setTinderMultiLeadSubmittingProperty(property.id));

            const leadFormValues: IApplicationFormValues = {
                name: contactValues.name,
                email: contactValues.email,
                phone: getPhoneDataFromString(contactValues.phone),
                marketing_email: contactValues.marketing_email,
                marketing_sms: contactValues.marketing_sms,
                additionalQuestions,
                text: getGeneratedMessage({
                    offerType: property.offer.type,
                    offerName: property.offer.name,
                    propertyNumber: property.number,
                    name: contactValues.name,
                    rooms: property.rooms,
                    area: property.area,
                    additionalQuestions: additionalQuestions.map((question) => question.label)
                })
            };

            const payload: IPostApplicationPayload = {
                source: ApplicationSource.RecommendedProperty,
                source_section: sourceSection,
                property: property.id,
                referer_application_uuid: refererApplicationUuid || undefined,
                ...getParsedLeadFormValues(leadFormValues)
            };

            try {
                const application = await createApplication(payload).unwrap();

                // Advance the deck + close the sheet FIRST, so the success UI never depends on the
                // best-effort bookkeeping below (which used to silently swallow any error in its
                // catch, leaving the sheet stuck open).
                dispatch(appendSentTinderMultiLeadApplication(getTinderMultiLeadSentItem(application, property)));

                trackTinderMultiLeadSurveyGenerate({property, application, additionalQuestions});

                // Algolytics
                hitApplicationRecommendedPropertiesSuccess(application, sourceSection, {extraPayloadViewType: viewType ?? undefined});
                hitPropertyMultileadSent([[application, property]], viewType, ApplicationMultiLeadRecommendationType.PROPERTY, application);

                // GTM purchase needs the full offer detail; fetched best-effort so tracking never blocks the send flow.
                dispatch(applicationLeadDataApi.endpoints.getApplicationOfferDetail.initiate({offerId: application.offer}))
                    .unwrap()
                    .then((offerDetail) => hitTinderMultiLeadGtmPurchase({application, offerDetail, property, viewType}))
                    .catch((gtmError) => notifyBugsnag(gtmError, "Tinder multilead: GTM purchase tracking failed"));

                try {
                    addOffersToStorageSentApplication([
                        {
                            id: property.offer.id,
                            coordinates: property.offer.geo_point.coordinates,
                            country: property.offer.region.country as Country,
                            regionId: getOfferRegionId(property)
                        }
                    ]);
                    addPropertiesToStorageSentApplication([property.id]);

                    if (isAuthenticated && application.offer) {
                        dispatch(addAppliedOffers([application.offer]));
                    }
                } catch (bookkeepingError) {
                    notifyBugsnag(bookkeepingError, "Tinder multilead: post-send bookkeeping failed");
                }

                return application;
            } catch (error) {
                notifyBugsnag(error, "Tinder multilead: create application failed");
                dispatch(setTinderMultiLeadError("Coś poszło nie tak. Spróbuj ponownie."));

                if (isFetchBaseQueryError(error)) {
                    trackTinderMultiLeadSurveyError({property, ...getAppErrorFromRtkError(error)});
                }

                return null;
            } finally {
                isInFlightRef.current = false;
            }
        },
        [createApplication, dispatch, isAuthenticated, userProfile, viewType]
    );

    return {
        isContactDataLoading: isAuthenticated && (isUserProfileFetching || isUserProfileLoading),
        sendTinderMultiLeadApplication
    };
}
import {useCallback, useRef} from "react";

import {isFetchBaseQueryError} from "@pg-mono/react-hook-forms-utils";

import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {getPhoneDataFromString} from "../../../form/utils/get_phone_data_from_string";
import {addFailureNotification} from "../../../notifications/utils/add_failure_notification";
import {addAppliedOffers} from "../../../offer/map/actions/add_applied_offers";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {isPropertyDetail} from "../../../property/utils/is_property_detail";
import {Country} from "../../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {hitApplicationRecommendedPropertiesSuccess} from "../../../tracking/algolytics_hits/application_hit";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {applicationLeadDataApi} from "../../api/application_lead_data_api";
import {useCreateApplicationMutation} from "../../api/create_application";
import {appendSentTinderMultiLeadApplication, setTinderMultiLeadError, setTinderMultiLeadSubmittingProperty} from "../../state/tinder_multi_lead_slice";
import {hitPropertyMultileadSent} from "../../tracking/lead/hit_property_multilead_sent";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IApplicationData} from "../../types/IApplicationData";
import {IPostApplicationPayload} from "../../types/lead/IPostApplicationPayload";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {addPropertiesToStorageSentApplication} from "../../utils/application_property_storage";
import {ApplicationSource, ApplicationSourceSection} from "../../utils/form/application_form_source";
import {getGeneratedMessage} from "../../utils/form/get_generated_message";
import {getAppErrorFromRtkError} from "../../utils/get_app_error_from_rtk_error";
import {getParsedLeadFormValues} from "../../utils/lead/get_parsed_lead_form_values";
import {addOffersToStorageSentApplication} from "../../utils/multi_lead/get_storage_sent_applications_id_list_by";
import {hitTinderMultiLeadGtmPurchase} from "./tracking/hit_tinder_multi_lead_gtm_purchase";
import {trackTinderMultiLeadSurveyError, trackTinderMultiLeadSurveyGenerate} from "./tracking/track_tinder_multi_lead_events";
import {getTinderMultiLeadContactValues, getTinderMultiLeadSentItem} from "./tinder_multi_lead_utils";

interface ISendParams {
    additionalQuestions: {value: string; label: string}[];
    property: IPropertyDetail | IRecommendedProperty;
    refererApplicationUuid: string | null;
    sourceSection: ApplicationSourceSection;
}

function getOfferRegionId(property: IPropertyDetail | IRecommendedProperty) {
    return isPropertyDetail(property) ? property.offer.region.id : property.region.id;
}

export function useTinderMultiLeadSend() {
    const dispatch = useAppDispatch();
    const isAuthenticated = useIsAuthenticated();
    const viewType = useAppSelector((store) => store.viewType.current);
    const {data: userProfile, isFetching: isUserProfileFetching, isLoading: isUserProfileLoading} = useGetUserProfileQuery();
    const [createApplication] = useCreateApplicationMutation();
    const isInFlightRef = useRef(false);

    const sendTinderMultiLeadApplication = useCallback(
        async ({additionalQuestions, property, refererApplicationUuid, sourceSection}: ISendParams): Promise<IApplicationData | null> => {
            if (isInFlightRef.current) {
                return null;
            }

            const contactValues = getTinderMultiLeadContactValues({userProfile});

            if (!contactValues) {
                dispatch(addFailureNotification("Nie znaleziono danych kontaktowych do wysłania zapytania."));
                return null;
            }

            isInFlightRef.current = true;
            dispatch(setTinderMultiLeadSubmittingProperty(property.id));

            const leadFormValues: IApplicationFormValues = {
                name: contactValues.name,
                email: contactValues.email,
                phone: getPhoneDataFromString(contactValues.phone),
                marketing_email: contactValues.marketing_email,
                marketing_sms: contactValues.marketing_sms,
                additionalQuestions,
                text: getGeneratedMessage({
                    offerType: property.offer.type,
                    offerName: property.offer.name,
                    propertyNumber: property.number,
                    name: contactValues.name,
                    rooms: property.rooms,
                    area: property.area,
                    additionalQuestions: additionalQuestions.map((question) => question.label)
                })
            };

            const payload: IPostApplicationPayload = {
                source: ApplicationSource.RecommendedProperty,
                source_section: sourceSection,
                property: property.id,
                referer_application_uuid: refererApplicationUuid || undefined,
                ...getParsedLeadFormValues(leadFormValues)
            };

            try {
                const application = await createApplication(payload).unwrap();

                // Advance the deck + close the sheet FIRST, so the success UI never depends on the
                // best-effort bookkeeping below (which used to silently swallow any error in its
                // catch, leaving the sheet stuck open).
                dispatch(appendSentTinderMultiLeadApplication(getTinderMultiLeadSentItem(application, property)));

                trackTinderMultiLeadSurveyGenerate({property, application, additionalQuestions});

                // Algolytics
                hitApplicationRecommendedPropertiesSuccess(application, sourceSection, {extraPayloadViewType: viewType ?? undefined});
                hitPropertyMultileadSent([[application, property]], viewType, ApplicationMultiLeadRecommendationType.PROPERTY, application);

                // GTM purchase needs the full offer detail; fetched best-effort so tracking never blocks the send flow.
                dispatch(applicationLeadDataApi.endpoints.getApplicationOfferDetail.initiate({offerId: application.offer}))
                    .unwrap()
                    .then((offerDetail) => hitTinderMultiLeadGtmPurchase({application, offerDetail, property, viewType}))
                    .catch((gtmError) => notifyBugsnag(gtmError, "Tinder multilead: GTM purchase tracking failed"));

                try {
                    addOffersToStorageSentApplication([
                        {
                            id: property.offer.id,
                            coordinates: property.offer.geo_point.coordinates,
                            country: property.offer.region.country as Country,
                            regionId: getOfferRegionId(property)
                        }
                    ]);
                    addPropertiesToStorageSentApplication([property.id]);

                    if (isAuthenticated && application.offer) {
                        dispatch(addAppliedOffers([application.offer]));
                    }
                } catch (bookkeepingError) {
                    notifyBugsnag(bookkeepingError, "Tinder multilead: post-send bookkeeping failed");
                }

                return application;
            } catch (error) {
                notifyBugsnag(error, "Tinder multilead: create application failed");
                dispatch(setTinderMultiLeadError("Coś poszło nie tak. Spróbuj ponownie."));

                if (isFetchBaseQueryError(error)) {
                    trackTinderMultiLeadSurveyError({property, ...getAppErrorFromRtkError(error)});
                }

                return null;
            } finally {
                isInFlightRef.current = false;
            }
        },
        [createApplication, dispatch, isAuthenticated, userProfile, viewType]
    );

    return {
        isContactDataLoading: isAuthenticated && (isUserProfileFetching || isUserProfileLoading),
        sendTinderMultiLeadApplication
    };
}
